import {
  AloftArea,
  AloftDate,
  AloftPrice,
  LatitudeLongitude,
  PropertyData,
  ToolkitListingID,
  expected,
  isDefined,
  schPropertyData,
} from '@aloft/toolkit-shared';

import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { CompSelectionProperty } from '../types.ts';
import { NewSimilarityScoreCalcOptions, addSimilarityScores } from './addSimilarityScores.ts';

const getCoordinates = (lat?: number, lng?: number) =>
  isDefined(lat) && isDefined(lng) ? { latitude: lat, longitude: lng } : undefined;

const getDistanceToSubject = (p1?: Partial<LatitudeLongitude>, p2?: Partial<LatitudeLongitude>) =>
  p1 && p2 ? +(proximityMiles(p1, p2)?.toFixed(2) ?? 0) : undefined;

const toAloftArea = (area?: { unitAmount: number; unit: 'sqft' | 'acre' }) =>
  area ? AloftArea.fromNumber(area.unitAmount, area.unit) : undefined;

const toAloftDate = (isoMonth?: string) =>
  isoMonth ? AloftDate.fromIsoString(`${isoMonth}-01`) : undefined;

const convertCompToCompSelectionProperty = (
  compID: string,
  comp: PropertyData,
  subject: CompSelectionProperty,
  newCalcOptions?: NewSimilarityScoreCalcOptions,
): CompSelectionProperty => {
  const { coordinates: coord, address: rawAddress } = comp.editableData.propertyIdentification;
  const address = expected(rawAddress);
  const { improvements, transaction, site } = comp.editableData;
  const { listing } = comp.readonlyData.backupListingData ?? {};

  const subjectCoords = getCoordinates(subject.latitude, subject.longitude);
  const compCoords = getCoordinates(coord?.latitude, coord?.longitude);
  const distanceToSubject = getDistanceToSubject(subjectCoords, compCoords);

  const preferredRowId = listing?.ToolkitListingID ?? listing?.ListingId ?? compID;

  const result: CompSelectionProperty = {
    t: 'tk',
    id: preferredRowId,
    address,
    yearBuilt: improvements.age ? AloftDate.today().getYear() - improvements.age : undefined,
    latitude: coord?.latitude,
    longitude: coord?.longitude,
    gla: toAloftArea(improvements.gla),
    lotSize: toAloftArea(site.area),
    saleDate: toAloftDate(transaction.saleDate),
    contractDate: toAloftDate(transaction.contractDate),
    salePrice: transaction.price ? AloftPrice.schema.parse(transaction.price) : undefined,
    listPrice: transaction.originalListPrice
      ? AloftPrice.schema.parse(transaction.originalListPrice)
      : undefined,
    distanceToSubject,
    cumulativeDaysOnMarket: transaction.daysOnMarket,
    bedrooms: listing?.BedroomsTotal,
    bedroomCount: listing?.BedroomsTotal,
    fullBaths: improvements.fullBaths,
    halfBaths: improvements.halfBaths,
    basement: listing?.BasementYN,
    garage: listing?.GarageYN,
    mlsStatus: listing?.MlsStatus,
    listingID: listing?.ToolkitListingID || (listing?.ListingId as ToolkitListingID),
    listingData: listing,
    reo: listing?.SpecialListingConditions?.includes('REO'),
    shortSale: listing?.SpecialListingConditions?.includes('Short Sale'),
    garageCount: listing?.GarageSpaces,
    uadQualityRating: improvements.uadQuality,
    uadConditionRating: improvements.uadCondition,
  };

  addSimilarityScores([result], subject, newCalcOptions);
  return result;
};

export const compsToCompSelectionProps = (
  compSeq: string[],
  comps: Record<string, PropertyData>,
  subjectComp: CompSelectionProperty,
  newCalcOptions?: NewSimilarityScoreCalcOptions,
): (CompSelectionProperty | undefined)[] =>
  compSeq.map((id) => {
    if (!schPropertyData.safeParse(comps[id]).success) return undefined;
    return convertCompToCompSelectionProperty(id, expected(comps[id]), subjectComp, newCalcOptions);
  });
